/**
 * PaymentOutcome (T35.7.2.2 post-payment UX) — the post-payment terminal state
 * for the donation / back-an-idea dialogs.
 *
 * CONTRACT — exactly TWO post-payment messages, do not add a third:
 *   1. success — fires on the CHARGE (`onResult(true)`), NOT on a webhook. The
 *      copy is UNIVERSAL: identical for a donation AND an idea-backing (no `kind`
 *      branch). The webhook still updates Settings → Billing in the background,
 *      but this message never waits on it (no polling, no pending state).
 *   2. failure — declined / errored / couldn't even start. In every such case
 *      the user was NOT charged, so the copy says so. The body carries the
 *      processor's specific reason when available, else a generic fallback.
 *
 * There is NO second thank-you anywhere else — not on the banner, not on the idea
 * cards. This dialog message is the only place a success message appears.
 *
 * Token-only (CP5); mobile-first; ≥44px targets.
 */

import { type CSSProperties } from 'react';

/** The universal success copy — IDENTICAL for donation and backing (no kind branch). */
export const SUCCESS_HEADING = 'Thank you for supporting Shopista 💜';
export const SUCCESS_BODY = 'Your contribution keeps a small, independent team building it.';

/** The failure copy — the heading is fixed; the body is the reason or a fallback. */
export const FAILURE_HEADING = "Your payment didn't go through — you weren't charged.";
export const FAILURE_FALLBACK = 'Something went wrong. Please try again.';

const wrapStyle: CSSProperties = {
  padding: 'var(--space-3)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.45,
};

const headingStyle: CSSProperties = {
  margin: '0 0 var(--space-2)',
  fontSize: 'var(--font-size-base)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
};

const errorHeadingStyle: CSSProperties = {
  ...headingStyle,
  color: 'var(--color-feedback-error)',
};

const actionRowStyle: CSSProperties = {
  marginTop: 'var(--space-4)',
  display: 'flex',
  gap: 'var(--space-2)',
};

const actionBtnStyle: CSSProperties = {
  flex: 1,
  minHeight: '2.75rem',
  border: 0,
  background: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

/**
 * The success message. Fires on the charge (`onResult(true)`); the same copy for
 * donation and backing. `Done` closes the dialog.
 */
export function PaymentSuccess({
  onDone,
  testIdPrefix,
}: {
  onDone: () => void;
  testIdPrefix: string;
}): JSX.Element {
  return (
    <div style={wrapStyle} role="status" data-testid={`${testIdPrefix}-success`}>
      <p style={headingStyle}>{SUCCESS_HEADING}</p>
      <p style={{ margin: 0 }} data-testid={`${testIdPrefix}-success-body`}>
        {SUCCESS_BODY}
      </p>
      <div style={actionRowStyle}>
        <button
          type="button"
          style={actionBtnStyle}
          onClick={onDone}
          data-testid={`${testIdPrefix}-success-done`}
        >
          Done
        </button>
      </div>
    </div>
  );
}

/**
 * The failure message. Shown when the payment didn't go through (declined,
 * errored, or couldn't start) — the user was NOT charged. `reason` is the
 * processor's specific detail when available; a generic fallback otherwise.
 * `Try again` returns to the pay/amount step and clears the error.
 */
export function PaymentFailure({
  reason,
  onRetry,
  testIdPrefix,
}: {
  reason?: string | undefined;
  onRetry: () => void;
  testIdPrefix: string;
}): JSX.Element {
  return (
    <div style={wrapStyle} role="alert" data-testid={`${testIdPrefix}-failed`}>
      <p style={errorHeadingStyle}>{FAILURE_HEADING}</p>
      <p style={{ margin: 0 }} data-testid={`${testIdPrefix}-failed-body`}>
        {reason && reason.trim() !== '' ? reason : FAILURE_FALLBACK}
      </p>
      <div style={actionRowStyle}>
        <button
          type="button"
          style={actionBtnStyle}
          onClick={onRetry}
          data-testid={`${testIdPrefix}-failed-retry`}
        >
          Try again
        </button>
      </div>
    </div>
  );
}
